import { CloseIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, FormControl, HStack, Text } from "@chakra-ui/react";
import { Dispatch, FC, SetStateAction } from "react";
import { errorColor } from "../../../utils/colors";
import { Toast } from "../../atom/Toast/Toast";
import SpinnerComponent from "../../atom/Spinner/SpinnerComponent";

interface FileUploadProps {
  file: File | null | undefined;
  setFile: Dispatch<SetStateAction<File | null | undefined>>;
  onFileChange: (file: File | null) => void;
  uploadLimit: number;
  buttonName: string;
  validExtensions: string[];
  shouldShowError?: boolean;
  loadingFile: boolean;
}

const FileUpload: FC<FileUploadProps> = ({
  file,
  setFile,
  onFileChange,
  uploadLimit,
  buttonName,
  validExtensions,
  shouldShowError = false,
  loadingFile = true,
}) => {
  const { addToast } = Toast();
  const acceptedExtensions = `.${validExtensions.join(",.")}`;

  const handleFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.[0];

    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop()?.toLowerCase();

      if (!validExtensions.includes(fileExtension || "")) {
        setFile(null);
        onFileChange(null);
        addToast({
          title: "Error",
          description: `Tipo de arquivo incorreto, deve ser ${acceptedExtensions}.`,
          status: "error",
        });

        return;
      }

      if (selectedFile.size > uploadLimit * 1024 * 1024) {
        setFile(null);
        onFileChange(null);
        addToast({
          title: "Error",
          description: `O tamanho do arquivo excede o limite de upload de ${uploadLimit} MB.`,
          status: "error",
        });
        return;
      }

      setFile(selectedFile);
      onFileChange(selectedFile);
    }
  };

  const handleButtonClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptedExtensions;
    input.onchange = handleFileChange;

    input.click();
  };

  const handleRemoveFile = () => {
    setFile(null);
    onFileChange(null);
  };

  return (
    <Box>
      <FormControl>
        {file || loadingFile ? (
          <HStack spacing={2}>
            {!loadingFile && file ? (
              <>
                <Button variant="tertiary" onClick={handleRemoveFile}>
                  {file.name}
                </Button>
                <CloseIcon
                  color={errorColor}
                  onClick={handleRemoveFile}
                  boxSize="3"
                />
              </>
            ) : (
              <Flex gap="2">
                <SpinnerComponent /> <Text>Buscando o arquivo...</Text>
              </Flex>
            )}
          </HStack>
        ) : (
          <Box>
            <Button
              variant="secondary"
              onClick={handleButtonClick}
              borderColor={shouldShowError ? errorColor : undefined}
            >
              {buttonName}
            </Button>
            {shouldShowError && (
              <Text fontSize="sm" color={errorColor}>
                Campo obrigatório não preenchido
              </Text>
            )}
          </Box>
        )}
      </FormControl>
    </Box>
  );
};

export default FileUpload;
